import { assert, isNone, Maybe } from '@crocodille/common';
import { RefObject, useEffect, useRef } from 'react';

export type UseEventListenerOptions = Pick<
  AddEventListenerOptions,
  'capture' | 'passive' | 'once'
>;

/**
 * We don't care about dispatching events in this case.
 */
type EventSource = Omit<EventTarget, 'dispatchEvent'>;

/**
 * Check if value is an event target.
 */
function isEventSource<T>(value: T): value is Extract<T, EventSource> {
  return (
    value &&
    typeof value === 'object' &&
    'addEventListener' in value &&
    'removeEventListener' in value
  );
}

/**
 * Adds an event listener to the given target
 *
 * @param eventName The name of the event to listen to
 * @param listener Listener function
 * @param targetRef The event target as react ref
 * @param options Options for the listener
 */
export function useEventListener<
  T extends HTMLElement,
  E extends keyof HTMLElementEventMap,
>(
  eventName: E,
  listener: (event: HTMLElementEventMap[E]) => void,
  targetRef: RefObject<T | null>,
  options?: UseEventListenerOptions,
): void;
export function useEventListener<E extends keyof WindowEventMap>(
  eventName: E,
  listener: (event: WindowEventMap[E]) => void,
  targetRef: Maybe<Window>,
  options?: UseEventListenerOptions,
): void;
export function useEventListener<E extends keyof DocumentEventMap>(
  eventName: E,
  listener: (event: DocumentEventMap[E]) => void,
  targetRef?: Maybe<Document>,
  options?: UseEventListenerOptions,
): void;
export function useEventListener<E extends Event = Event>(
  eventName: string,
  listener: (e: E) => void,
  targetRef:
    | RefObject<HTMLElement | null>
    | Maybe<Window | Document> = typeof window !== 'undefined'
    ? document
    : null,
  options: UseEventListenerOptions = {},
): void {
  //
  // To avoid the need of useCallback, we keep the active listener fn in a Ref and update it via effect.
  //
  const activeListener = useRef(listener);
  useEffect(() => {
    activeListener.current = listener;
  }, [listener]);

  const { capture, passive, once } = options;

  //
  // The resulting registration of a listener then doesn't need to change when
  // callback function changes.
  //
  useEffect(() => {
    const source = isEventSource(targetRef) ? targetRef : targetRef?.current;

    assert(isNone(source) || isEventSource(source));

    const callback = (e: E) => activeListener.current(e);
    source?.addEventListener?.(eventName, callback as EventListener, {
      capture,
      passive,
      once,
    });

    return () =>
      source?.removeEventListener?.(eventName, callback as EventListener);
  }, [eventName, targetRef, capture, passive, once]);
}
